import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = { nombre_completo: '', cedula: '', telefono: '', licencia: '', estado: 'activo' };

export const ConductorDialog = ({ open, onOpenChange, conductor, onSuccess }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (conductor) {
      setForm({
        nombre_completo: conductor.nombre_completo || '',
        cedula: conductor.cedula || '',
        telefono: conductor.telefono || '',
        licencia: conductor.licencia || '',
        estado: conductor.estado || 'activo'
      });
    } else {
      setForm(empty);
    }
  }, [conductor, open]);

  const handleChange = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (conductor) {
        await axios.patch(`${API_URL}/api/conductores/${conductor.id}`, form, { withCredentials: true });
        toast.success('Conductor actualizado');
      } else {
        await axios.post(`${API_URL}/api/conductores`, form, { withCredentials: true });
        toast.success('Conductor creado');
      }
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            {conductor ? 'Editar Conductor' : 'Nuevo Conductor'}
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {conductor ? 'Modifica los datos del conductor' : 'Registra un nuevo conductor'}
          </DialogDescription>
        </DialogHeader>

        <form data-testid="conductor-form" onSubmit={submit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Nombre Completo *</Label>
            <Input
              data-testid="cond-nombre-input"
              value={form.nombre_completo}
              onChange={(e) => handleChange('nombre_completo', e.target.value)}
              placeholder="Juan Pérez López"
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Cédula</Label>
              <Input
                data-testid="cond-cedula-input"
                value={form.cedula}
                onChange={(e) => handleChange('cedula', e.target.value)}
                placeholder="1717171717"
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Teléfono *</Label>
              <Input
                data-testid="cond-telefono-input"
                type="tel"
                value={form.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                placeholder="+593987654321"
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Licencia</Label>
              <Input
                data-testid="cond-licencia-input"
                value={form.licencia}
                onChange={(e) => handleChange('licencia', e.target.value)}
                placeholder="Tipo B"
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Estado *</Label>
              <Select value={form.estado} onValueChange={(v) => handleChange('estado', v)}>
                <SelectTrigger data-testid="cond-estado-select" className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="inactivo">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              data-testid="cond-submit-button"
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] transition-colors"
            >
              {loading ? 'Guardando...' : (conductor ? 'Guardar cambios' : 'Crear conductor')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
